/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 水印应用逻辑 Hook（批量应用和重新应用）
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-01-07 20:40:13
 */

import React, { useCallback } from 'react'
import { message } from 'antd'
import { useAITaskEvent, fetchOutputFromUrl } from '../../hooks/useAITaskEvent'
import { useWatermarkExtraction } from '../../hooks/useWatermarkExtraction'
import {
  WatermarkExtractionStatus,
  PhotoItem,
  WatermarkItem
} from '../../context/WatermarkBatchContext'
import {
  WatermarkApplyStatus,
  WatermarkApplyOutputResult
} from '../../hooks/useWatermarkApply'
import { TestDataItem } from './useCSVUpload'
import { useUploadDataToOSS } from './useUploadDataToOSS'

interface UseWatermarkApplyLogicParams {
  dataSource: TestDataItem[]
  currentPage: number
  pageSize: number
  userId: string | undefined
  updateDataSource: (updater: (prev: TestDataItem[]) => TestDataItem[]) => void
  setApplying: (applying: boolean) => void
  setHasCompletedApply: (completed: boolean) => void
  setProcessedDataKeysSet: React.Dispatch<React.SetStateAction<Set<string>>>
}

export const useWatermarkApplyLogic = ({
  dataSource,
  currentPage,
  pageSize,
  userId,
  updateDataSource,
  setApplying,
  setHasCompletedApply,
  setProcessedDataKeysSet
}: UseWatermarkApplyLogicParams) => {
  const { createAITask, pollAITaskResult } = useAITaskEvent()
  const { extractWatermarks } = useWatermarkExtraction()
  const { uploadDataToOSS, generateKey, uploadKeyList } = useUploadDataToOSS()

  // 第二步：应用水印到当前页数据
  const handleApply = useCallback(async () => {
    if (dataSource.length === 0) {
      message.warning('请先上传测试数据')
      return
    }

    // 计算当前页的数据范围
    const startIndex = (currentPage - 1) * pageSize
    const endIndex = startIndex + pageSize
    const currentPageData = dataSource.slice(startIndex, endIndex)

    if (currentPageData.length === 0) {
      message.warning('当前页没有数据')
      return
    }

    // 检查是否有提取完成的数据和 diff 数据（排除已有 autoUrl 的项）
    const completedData = currentPageData.filter(
      (item) =>
        !item.autoUrl && // 排除已有自动处理链接的项
        item.extractionStatus === WatermarkExtractionStatus.COMPLETED &&
        item.watermarkData &&
        item.watermarkData.length > 0 &&
        item.diff // 必须有 diff 数据
    )

    if (completedData.length === 0) {
      message.warning(
        '当前页没有已提取完成的数据或缺少 diff 数据，请先执行第一步：提取'
      )
      return
    }

    setApplying(true)
    const hideLoading = message.loading('应用水印中，请耐心等待...', 0)

    // 处理单张图片的函数
    const processSingleItem = async (
      currentItem: TestDataItem,
      index: number
    ): Promise<void> => {
      const startTime = Date.now()

      if (!currentItem.diff) {
        console.warn(`第 ${index + 1} 张图片缺少 diff 数据，跳过`)
        return
      }

      // 解析当前图片的 diff 列的 JSON 数据
      let diffData: Array<{
        id: number
        title: string
        content: string
        editType: string
        old_title: string
        old_content: string
      }> = []

      try {
        diffData = JSON.parse(currentItem.diff)
        if (!Array.isArray(diffData)) {
          throw new Error('diff 数据格式错误：不是数组')
        }
      } catch (error) {
        console.error(`第 ${index + 1} 张图片解析 diff 数据失败:`, error)
        const duration = Date.now() - startTime
        updateDataSource((prev) => {
          const updated = [...prev]
          const itemIndex = updated.findIndex((d) => d.key === currentItem.key)
          if (itemIndex !== -1) {
            updated[itemIndex] = {
              ...updated[itemIndex],
              applyStatus: WatermarkApplyStatus.FAILED,
              applyShowReason: '解析 diff 数据失败，请检查数据格式',
              applyDuration: duration
            }
          }
          return updated
        })
        return
      }

      // 从 watermarkData 中创建 id 到完整数据的映射
      const watermarkDataMap = new Map<string, WatermarkItem>()
      if (currentItem.watermarkData) {
        currentItem.watermarkData.forEach((item) => {
          const key = String(item.item_id || item.id || '')
          watermarkDataMap.set(key, item)
        })
      }

      // 构建 src_list
      const srcList = diffData
        .map((diffItem) => {
          if (diffItem.editType === '新增') {
            return {
              item_id: -1,
              title: '',
              content: '',
              switch: false,
              item_status: 1,
              edit_type: 3
            }
          }

          const id = String(diffItem.id)
          const watermarkItem = watermarkDataMap.get(id)

          return {
            item_id: diffItem.id,
            title: diffItem.old_title || '',
            content: diffItem.old_content || '',
            switch: watermarkItem?.switch ?? true,
            item_status: watermarkItem?.item_status ?? 0,
            edit_type: 3
          }
        })
        .filter((item) => {
          const editType = item.edit_type
          return editType === 2 || editType === 3 || editType === 5
        })

      // 构建 edit_list
      const editList = diffData
        .map((diffItem) => {
          const id = String(diffItem.id)
          const watermarkItem = watermarkDataMap.get(id)

          let itemStatus = watermarkItem?.item_status ?? 3
          if (diffItem.editType === '修改') {
            itemStatus = 2
          } else if (diffItem.editType === '新增') {
            itemStatus = 1
          } else if (diffItem.editType === '删除') {
            itemStatus = 0
          }

          return {
            item_id: diffItem.id,
            title: diffItem.title || '',
            content: diffItem.content || '',
            switch: diffItem.editType === '删除' ? false : true,
            item_status: itemStatus ?? 0,
            edit_type: 3
          }
        })
        .filter((item) => {
          const editType = item.edit_type
          return editType === 2 || editType === 3 || editType === 5
        })

      // 获取当前图片的 URL
      const currentUrl = currentItem.oldUrl
        ? currentItem.oldUrl.replace(/`/g, '').trim()
        : ''

      if (!currentUrl) {
        console.warn(`第 ${index + 1} 张图片缺少 URL，跳过`)
        const duration = Date.now() - startTime
        updateDataSource((prev) => {
          const updated = [...prev]
          const itemIndex = updated.findIndex((d) => d.key === currentItem.key)
          if (itemIndex !== -1) {
            updated[itemIndex] = {
              ...updated[itemIndex],
              applyStatus: WatermarkApplyStatus.FAILED,
              applyShowReason: '缺少 URL',
              applyDuration: duration
            }
          }
          return updated
        })
        return
      }

      // 创建 AI 任务
      const taskResult = await createAITask({
        aiTaskType: 14,
        userID: userId || '',
        input: {
          urls: [],
          select_url: currentUrl,
          src_list: srcList,
          edit_list: editList,
          edit_user: userId || ''
        }
      })

      const taskID = taskResult?.data?.taskID || ''
      if (!taskID) {
        console.error(`第 ${index + 1} 张图片创建应用任务失败`)
        const duration = Date.now() - startTime
        updateDataSource((prev) => {
          const updated = [...prev]
          const itemIndex = updated.findIndex((d) => d.key === currentItem.key)
          if (itemIndex !== -1) {
            updated[itemIndex] = {
              ...updated[itemIndex],
              applyStatus: WatermarkApplyStatus.FAILED,
              applyShowReason: '创建应用任务失败',
              applyDuration: duration
            }
          }
          return updated
        })
        return
      }

      // 轮询任务结果
      const result = await pollAITaskResult(
        {
          taskID: taskID,
          userID: userId || ''
        },
        { photoCount: 1, timeout: 120000 }
      )

      // 处理返回结果
      if (result.data?.output && result.data?.taskStatus === 3) {
        try {
          const isUrl =
            typeof result.data.output === 'string' &&
            (result.data.output.startsWith('http://') ||
              result.data.output.startsWith('https://'))

          let outputData: WatermarkApplyOutputResult[]

          if (isUrl) {
            outputData = await fetchOutputFromUrl<WatermarkApplyOutputResult[]>(
              result.data.output
            )
          } else {
            outputData = JSON.parse(result.data.output)
          }

          if (Array.isArray(outputData) && outputData.length > 0) {
            const outputItem = outputData[0]
            const applyStatus = Number(outputItem.success)
            const duration = Date.now() - startTime
            const newAutoUrl =
              applyStatus === WatermarkApplyStatus.EDITED && outputItem.res
                ? outputItem.res
                : undefined

            updateDataSource((prev) => {
              const updated = [...prev]
              const itemIndex = updated.findIndex(
                (d) => d.key === currentItem.key
              )

              if (itemIndex !== -1) {
                updated[itemIndex] = {
                  ...updated[itemIndex],
                  applyStatus: applyStatus,
                  applyShowReason:
                    outputItem.show_reason || outputItem.reason || '',
                  applyDuration: duration,
                  applyTaskID: taskID,
                  autoUrl: newAutoUrl || updated[itemIndex].autoUrl
                }

                // 如果成功且有新的 autoUrl，异步上传到 OSS
                if (applyStatus === WatermarkApplyStatus.EDITED && newAutoUrl) {
                  const originalUrl = currentItem.oldUrl || ''
                  const manualEditUrl = currentItem.newUrl || ''
                  const itemKey = generateKey(originalUrl, manualEditUrl)

                  uploadDataToOSS({
                    originalUrl,
                    manualEditUrl,
                    remark: updated[itemIndex].remark || '',
                    isProcessed: updated[itemIndex].isProcessed || false,
                    processedBy: updated[itemIndex].processedBy,
                    autoUrl: newAutoUrl
                  })
                    .then(() => {
                      setProcessedDataKeysSet((prev) => {
                        const newSet = new Set(prev)
                        if (!newSet.has(itemKey)) {
                          newSet.add(itemKey)
                          const allKeys: string[] = []
                          newSet.forEach((k) => allKeys.push(k))
                          uploadKeyList(allKeys).catch((error) => {
                            console.error('更新数据key列表到OSS失败:', error)
                          })
                          console.log(
                            '数据key列表已更新到OSS，共',
                            allKeys.length,
                            '个key'
                          )
                        }
                        return newSet
                      })
                    })
                    .catch((error) => {
                      console.error('上传处理数据到OSS失败:', error)
                    })
                }
              }
              return updated
            })
          }
        } catch (error) {
          console.error(`第 ${index + 1} 张图片解析应用结果失败:`, error)
          const duration = Date.now() - startTime
          updateDataSource((prev) => {
            const updated = [...prev]
            const itemIndex = updated.findIndex(
              (d) => d.key === currentItem.key
            )
            if (itemIndex !== -1) {
              updated[itemIndex] = {
                ...updated[itemIndex],
                applyStatus: WatermarkApplyStatus.FAILED,
                applyShowReason: '解析应用结果失败',
                applyDuration: duration,
                applyTaskID: taskID
              }
            }
            return updated
          })
        }
      } else {
        const duration = Date.now() - startTime
        updateDataSource((prev) => {
          const updated = [...prev]
          const itemIndex = updated.findIndex((d) => d.key === currentItem.key)
          if (itemIndex !== -1) {
            updated[itemIndex] = {
              ...updated[itemIndex],
              applyStatus: WatermarkApplyStatus.FAILED,
              applyShowReason: '应用失败，请稍后重试',
              applyDuration: duration,
              applyTaskID: taskID
            }
          }
          return updated
        })
      }
    }

    try {
      const CONCURRENT_LIMIT = 5
      for (let i = 0; i < completedData.length; i += CONCURRENT_LIMIT) {
        const batch = completedData.slice(i, i + CONCURRENT_LIMIT)
        const batchPromises = batch.map((item, batchIndex) =>
          processSingleItem(item, i + batchIndex)
        )
        await Promise.all(batchPromises)
      }

      hideLoading()
      setHasCompletedApply(true)
      message.success(`应用完成，共处理 ${completedData.length} 张图片`)
    } catch (error) {
      hideLoading()
      setApplying(false)
      console.error('应用失败:', error)
      message.error('应用水印失败，请稍后重试')
    } finally {
      setApplying(false)
    }
  }, [
    dataSource,
    currentPage,
    pageSize,
    userId,
    createAITask,
    pollAITaskResult,
    updateDataSource,
    generateKey,
    uploadDataToOSS,
    setProcessedDataKeysSet,
    uploadKeyList,
    setApplying,
    setHasCompletedApply
  ])

  // 重新应用单张图片
  const handleReapplySingle = useCallback(
    async (record: TestDataItem) => {
      if (!record.diff) {
        message.warning('该图片缺少 diff 数据，无法重新应用')
        return
      }

      // 如果缺少水印数据，先执行提取
      let currentRecord = record
      if (!record.watermarkData || record.watermarkData.length === 0) {
        const hideExtractLoading = message.loading(
          '正在提取水印数据，请稍候...',
          0
        )

        try {
          const currentUrl = record.oldUrl
            ? record.oldUrl.replace(/`/g, '').trim()
            : ''

          if (!currentUrl) {
            hideExtractLoading()
            message.error('缺少图片 URL')
            return
          }

          const photo: PhotoItem = {
            seq: 1,
            url: record.oldUrl,
            aiURL: '',
            smallURL: currentUrl,
            watermarkStatus: WatermarkExtractionStatus.EXTRACTING
          }

          updateDataSource((prev) => {
            const updated = [...prev]
            const index = updated.findIndex((d) => d.key === record.key)
            if (index !== -1) {
              updated[index] = {
                ...updated[index],
                extractionStatus: WatermarkExtractionStatus.EXTRACTING
              }
            }
            return updated
          })

          const extractPromise = new Promise<TestDataItem>(
            (resolve, reject) => {
              let watermarkDataResolved = false
              const timeout = setTimeout(() => {
                if (!watermarkDataResolved) {
                  reject(new Error('提取水印数据超时'))
                }
              }, 60000)

              extractWatermarks(
                [photo],
                (
                  updatedPhotos: PhotoItem[],
                  status: WatermarkExtractionStatus
                ) => {
                  updateDataSource((prev) => {
                    const updated = [...prev]
                    const photoUrl = updatedPhotos[0]?.smallURL
                      ? updatedPhotos[0].smallURL.replace(/`/g, '').trim()
                      : ''

                    const index = updated.findIndex((d) => {
                      const dataUrl = d.oldUrl
                        ? d.oldUrl.replace(/`/g, '').trim()
                        : ''
                      return dataUrl === photoUrl
                    })

                    if (index !== -1) {
                      updated[index] = {
                        ...updated[index],
                        extractionStatus: status,
                        taskID: updatedPhotos[0]?.taskID,
                        errorMsg: updatedPhotos[0]?.errorMsg
                      }
                    }
                    return updated
                  })
                },
                (
                  photoUrl: string,
                  watermarkID: string,
                  watermarkData: WatermarkItem[]
                ) => {
                  updateDataSource((prev) => {
                    const updated = [...prev]
                    const index = updated.findIndex((d) => {
                      const dataUrl = d.oldUrl
                        ? d.oldUrl.replace(/`/g, '').trim()
                        : ''
                      const url = photoUrl
                        ? photoUrl.replace(/`/g, '').trim()
                        : ''
                      return dataUrl === url
                    })
                    if (index !== -1) {
                      updated[index] = {
                        ...updated[index],
                        watermarkData,
                        watermarkID
                      }
                      currentRecord = updated[index]
                      if (
                        currentRecord.watermarkData &&
                        currentRecord.watermarkData.length > 0 &&
                        currentRecord.extractionStatus ===
                          WatermarkExtractionStatus.COMPLETED
                      ) {
                        watermarkDataResolved = true
                        clearTimeout(timeout)
                        resolve(currentRecord)
                      }
                    }
                    return updated
                  })
                },
                () => {
                  hideExtractLoading()
                  updateDataSource((prev) => {
                    const updated = [...prev]
                    const index = updated.findIndex((d) => d.key === record.key)
                    const item = index !== -1 ? updated[index] : null
                    if (
                      item &&
                      item.watermarkData &&
                      item.watermarkData.length > 0 &&
                      !watermarkDataResolved
                    ) {
                      currentRecord = item
                      watermarkDataResolved = true
                      clearTimeout(timeout)
                      resolve(currentRecord)
                    }
                    return updated
                  })
                }
              ).catch((error) => {
                clearTimeout(timeout)
                reject(error)
              })
            }
          )

          try {
            currentRecord = await extractPromise
          } catch (error) {
            hideExtractLoading()
            message.error('提取水印数据失败或超时')
            return
          }
        } catch (error) {
          hideExtractLoading()
          console.error('提取水印数据失败:', error)
          message.error('提取水印数据失败')
          return
        }
      }

      const hideLoading = message.loading('重新应用中，请耐心等待...', 0)

      try {
        const startTime = Date.now()

        let diffData: Array<{
          id: number
          title: string
          content: string
          editType: string
          old_title: string
          old_content: string
        }> = []

        try {
          diffData = JSON.parse(record.diff)
          if (!Array.isArray(diffData)) {
            throw new Error('diff 数据格式错误：不是数组')
          }
        } catch (error) {
          hideLoading()
          message.error('解析 diff 数据失败，请检查数据格式')
          return
        }

        const watermarkDataMap = new Map<string, WatermarkItem>()
        if (currentRecord.watermarkData) {
          currentRecord.watermarkData.forEach((item) => {
            const key = String(item.item_id || item.id || '')
            watermarkDataMap.set(key, item)
          })
        }

        const srcList = diffData
          .map((diffItem) => {
            if (diffItem.editType === '新增') {
              return {
                item_id: -1,
                title: '',
                content: '',
                switch: false,
                item_status: 1,
                edit_type: 3
              }
            }

            const id = String(diffItem.id)
            const watermarkItem = watermarkDataMap.get(id)

            return {
              item_id: diffItem.id,
              title: diffItem.old_title || '',
              content: diffItem.old_content || '',
              switch: watermarkItem?.switch ?? true,
              item_status: watermarkItem?.item_status ?? 0,
              edit_type: 3
            }
          })
          .filter((item) => {
            const editType = item.edit_type
            return editType === 2 || editType === 3 || editType === 5
          })

        const editList = diffData
          .map((diffItem) => {
            const id = String(diffItem.id)
            const watermarkItem = watermarkDataMap.get(id)

            let itemStatus = watermarkItem?.item_status ?? 3
            if (diffItem.editType === '修改') {
              itemStatus = 2
            } else if (diffItem.editType === '新增') {
              itemStatus = 1
            } else if (diffItem.editType === '删除') {
              itemStatus = 0
            }

            return {
              item_id: diffItem.id,
              title: diffItem.title || '',
              content: diffItem.content || '',
              switch: diffItem.editType === '删除' ? false : true,
              item_status: itemStatus ?? 0,
              edit_type: 3
            }
          })
          .filter((item) => {
            const editType = item.edit_type
            return editType === 2 || editType === 3 || editType === 5
          })

        const currentUrl = currentRecord.oldUrl
          ? currentRecord.oldUrl.replace(/`/g, '').trim()
          : ''

        if (!currentUrl) {
          hideLoading()
          message.error('缺少图片 URL')
          return
        }

        const taskResult = await createAITask({
          aiTaskType: 14,
          userID: userId || '',
          input: {
            urls: [],
            select_url: currentUrl,
            src_list: srcList,
            edit_list: editList,
            edit_user: userId || ''
          }
        })

        const taskID = taskResult?.data?.taskID || ''
        if (!taskID) {
          hideLoading()
          message.error('创建应用任务失败')
          return
        }

        const result = await pollAITaskResult(
          {
            taskID: taskID,
            userID: userId || ''
          },
          { photoCount: 1, timeout: 120000 }
        )

        if (result.data?.output && result.data?.taskStatus === 3) {
          try {
            const isUrl =
              typeof result.data.output === 'string' &&
              (result.data.output.startsWith('http://') ||
                result.data.output.startsWith('https://'))

            let outputData: WatermarkApplyOutputResult[]

            if (isUrl) {
              outputData = await fetchOutputFromUrl<
                WatermarkApplyOutputResult[]
              >(result.data.output)
            } else {
              outputData = JSON.parse(result.data.output)
            }

            if (Array.isArray(outputData) && outputData.length > 0) {
              const outputItem = outputData[0]
              const applyStatus = Number(outputItem.success)
              const duration = Date.now() - startTime

              updateDataSource((prev) => {
                const updated = [...prev]
                const itemIndex = updated.findIndex(
                  (d) => d.key === currentRecord.key
                )

                if (itemIndex !== -1) {
                  const newAutoUrl =
                    applyStatus === WatermarkApplyStatus.EDITED &&
                    outputItem.res
                      ? outputItem.res
                      : updated[itemIndex].autoUrl

                  updated[itemIndex] = {
                    ...updated[itemIndex],
                    applyStatus: applyStatus,
                    applyShowReason:
                      outputItem.show_reason || outputItem.reason || '',
                    applyDuration: duration,
                    applyTaskID: taskID,
                    autoUrl: newAutoUrl
                  }

                  if (
                    applyStatus === WatermarkApplyStatus.EDITED &&
                    newAutoUrl
                  ) {
                    const originalUrl = currentRecord.oldUrl || ''
                    const manualEditUrl = currentRecord.newUrl || ''
                    const itemKey = generateKey(originalUrl, manualEditUrl)

                    uploadDataToOSS({
                      originalUrl,
                      manualEditUrl,
                      remark: updated[itemIndex].remark || '',
                      isProcessed: updated[itemIndex].isProcessed || false,
                      processedBy: updated[itemIndex].processedBy,
                      autoUrl: newAutoUrl
                    })
                      .then(() => {
                        setProcessedDataKeysSet((prev) => {
                          const newSet = new Set(prev)
                          if (!newSet.has(itemKey)) {
                            newSet.add(itemKey)
                            const allKeys: string[] = []
                            newSet.forEach((k) => allKeys.push(k))
                            uploadKeyList(allKeys).catch((error) => {
                              console.error('更新数据key列表到OSS失败:', error)
                            })
                            console.log(
                              '数据key列表已更新到OSS，共',
                              allKeys.length,
                              '个key'
                            )
                          }
                          return newSet
                        })
                      })
                      .catch((error) => {
                        console.error('上传处理数据到OSS失败:', error)
                      })
                  }
                }
                return updated
              })

              hideLoading()
              if (applyStatus === WatermarkApplyStatus.EDITED) {
                setHasCompletedApply(true)
                message.success('重新应用成功')
              } else {
                message.warning(
                  `重新应用失败: ${
                    outputItem.show_reason || outputItem.reason || '未知错误'
                  }`
                )
              }
            }
          } catch (error) {
            hideLoading()
            console.error('解析应用结果失败:', error)
            message.error('解析应用结果失败')
          }
        } else {
          hideLoading()
          message.error('应用失败，请稍后重试')
        }
      } catch (error) {
        hideLoading()
        console.error('重新应用失败:', error)
        message.error('重新应用失败，请稍后重试')
      }
    },
    [
      createAITask,
      pollAITaskResult,
      userId,
      updateDataSource,
      generateKey,
      uploadDataToOSS,
      extractWatermarks,
      setProcessedDataKeysSet,
      uploadKeyList,
      setHasCompletedApply
    ]
  )

  return {
    handleApply,
    handleReapplySingle
  }
}
